import { AnnouncementsModule } from '@b2b/common/components/announcements/announcements.component';
import {
  CartDirectiveModule,
  ContentHeaderDirectiveModule,
  HeaderDirectiveModule,
  NotesDirectiveModule,
  OverlayStatusDirectiveModule,
  SidebarDirectiveModule,
} from '@b2b/common/directives';
import { NavigationDirectiveModule } from '@b2b/common/directives/navigation/navigation.directive';
import { ThemesDataResolver, UserConfigDataResolver } from '@b2b/common/resolvers';
import uiRouter, { StateProvider } from '@uirouter/angularjs';
import agGrid from 'ag-grid';
import angular from 'angular';
import { ApplicationControllerModule } from './controllers/ApplicationController';
import {
  AccountView,
  CardholdersView,
  CartView,
  CouponsView,
  CustomersView,
  DashboardView,
  DonationsView,
  EntitiesView,
  FinanceView,
  FormsView,
  GroupsView,
  ImportExportView,
  InventoryView,
  LayoutsView,
  MembershipView,
  OrdersView,
  PartnersView,
  PassesView,
  AppStoreView,
  PermissionsView,
  PosView,
  PricingView,
  ProgramsView,
  ReportsView,
  ResourcesView,
  RetailView,
  RolesView,
  SchedulerView,
  SettingsView,
  SettlementView,
  SherpaView,
  SubscriptionsView,
  TemplatesView,
  ThemesView,
  UsersView,
} from './views';

/**
 * The Application module is the main entry point for the application
 *
 * Other views are located in the `src/app/views` directory and are loaded
 * into the context of application.default as subviews of the "main" ui-view
 */
agGrid.initialiseAgGridWithAngular1(angular);

export const ApplicationView = angular
  .module('acme.view.application', [
    uiRouter,
    'ngAnimate',
    'acme.common.directive.permit',
    HeaderDirectiveModule,
    OverlayStatusDirectiveModule,
    'acme.common.directive.footer',
    'acme.common.directive.search',
    AnnouncementsModule,
    NavigationDirectiveModule,
    'acme.common.directive.navExpanded',
    'acme.common.directive.navFocus',
    'acme.common.directive.screenReaderNavSelected',
    'acme.common.directive.viewMenu',
    SidebarDirectiveModule,
    ContentHeaderDirectiveModule,
    'acme.common.directive.contentSubheader',
    'acme.common.directive.perfectScrollbar',
    'acme.common.directive.history',
    'acme.common.directive.auditTrail',
    NotesDirectiveModule,
    'acme.common.directive.capitalize',
    'acme.common.directive.dateConverter',
    'acme.common.directive.kbDraggable',
    'acme.common.directive.summernote',
    'acme.common.directive.modalTitle',
    'acme.common.directive.printerFriendlyLink',
    'acme.common.directive.emailResources',
    CartDirectiveModule,
    DashboardView,
    OrdersView,
    CustomersView,
    FormsView,
    ProgramsView,
    RetailView,
    TemplatesView,
    ResourcesView,
    MembershipView,
    AccountView,
    EntitiesView,
    UsersView,
    InventoryView,
    ImportExportView,
    DonationsView,
    PartnersView,
    PricingView,
    CouponsView,
    ReportsView,
    FinanceView,
    ThemesView,
    SchedulerView,
    SettlementView,
    SettingsView,
    GroupsView,
    PermissionsView,
    RolesView,
    SherpaView,
    SubscriptionsView,
    PosView,
    LayoutsView,
    PassesView,
    CartView,
    CardholdersView,
    AppStoreView,
    ApplicationControllerModule,
    'acme.common.service.api',
    'acme.common.service.auth',
    'acme.view.pageNotFound.controllers',
    'acme.view.resolveFailed.controllers',
    'acme.view.downtime.controllers',
  ])

  /**
   * Each section or module of the site can also have its own routes. AngularJS
   * will handle ensuring they are all available at run-time, but splitting it
   * this way makes each module more "self-contained".
   *
   * Routes will be added into the application by the $stateProvider in individual views
   */
  .config(function config($stateProvider: StateProvider) {
    const application = {
      name: 'application',
      url: '/app',
      abstract: true,
      views: {
        main: {
          controller: 'ApplicationController',
          templateUrl: 'layouts/application.tpl.html',
        },
      },
      data: {
        pageTitle: 'Home',
        pageClass: 'application',
        roles: ['admin', 'user'],
      },
      resolve: {
        UserConfigData: UserConfigDataResolver(),
        ThemesData: ThemesDataResolver({ includeEmail: true }),
      },
    };

    const applicationDefault = {
      name: 'application.default',
      url: '',
      views: {
        'subview-main': {
          templateUrl: 'views/application/application.tpl.html',
        },
      },
    };

    const applicationFailedResolve = {
      name: 'application.failed',
      url: '/error',
      views: {
        'subview-main': {
          controller: 'ResolveFailedController',
          templateUrl: 'common/views/resolveFailed/resolveFailed.tpl.html',
        },
      },
    };

    const applicationPageNotFound = {
      name: 'application.notfound',
      url: '/notfound',
      views: {
        'subview-main': {
          controller: 'PageNotFoundController',
          templateUrl: 'common/views/pageNotFound/pageNotFound.tpl.html',
        },
      },
    };

    const applicationDowntime = {
      name: 'application.downtime',
      url: '/downtime',
      views: {
        'subview-main': {
          controller: 'DowntimeController',
          templateUrl: 'common/views/downtime/downtime.tpl.html',
        },
      },
    };

    $stateProvider
      .state(application)
      .state(applicationDefault)
      .state(applicationFailedResolve)
      .state(applicationPageNotFound)
      .state(applicationDowntime);
  }).name;
